import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './App.css';

// Components
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import PositionsGrid from './components/PositionsGrid';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';
import BackgroundBlobs from './components/BackgroundBlobs';
import AdminPanel from './components/AdminPanel';

// Constants (Still used for non-dynamic branding if needed)
import { BOARD_YEAR, CLUB_NAME } from './constants';
import { API_BASE } from './api';

/**
 * Root application component.
 * Now dynamic: Fetches configuration from the PHP backend.
 */
const App = () => {
  const [positions, setPositions] = useState([]);
  const [deadline, setDeadline] = useState(null);
  const [isClosed, setIsClosed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Simple routing for Admin Panel (supports /admin, /admin/, or ?admin=true)
  const isAdmin = /\/admin\/?$/.test(window.location.pathname) || window.location.search.includes('admin=true');

  useEffect(() => {
    // Only fetch dynamic data if NOT in admin panel
    if (isAdmin) return;

    const fetchLiveConfig = async () => {
      try {
        const response = await fetch(`${API_BASE}/get_data.php`);
        const result = await response.json();
        
        if (result.status === 'success') {
          setPositions(result.data.positions);
          setDeadline(result.data.settings.DEADLINE_DATE);
        } else {
          setError(result.message);
        }
      } catch (err) {
        console.error("Failed to load DB config, falling back...", err);
        setError("Failed to connect to database. Make sure XAMPP is running and init_db.php was executed.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLiveConfig();
  }, [isAdmin]);

  // Deadline checker logic
  useEffect(() => {
    if (isAdmin || isLoading || !deadline) return;

    const checkDeadline = () => {
      const now = new Date();
      const closeTime = new Date(deadline);
      if (now > closeTime && !isClosed) {
        setIsClosed(true);
      }
    };

    checkDeadline();
    const interval = setInterval(checkDeadline, 1000);
    return () => clearInterval(interval);
  }, [deadline, isClosed, isAdmin, isLoading]);

  // ── Render Admin Route ──
  if (isAdmin) {
    return <AdminPanel />;
  }

  // ── Render Loading/Error State ──
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
        <div className="bg-red-950/20 text-red-400 p-6 rounded-2xl shadow-sm text-center max-w-md border border-red-900/30">
          <h2 className="font-bold text-lg mb-2">System Offline</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // ── Deadline Passed Screen ──
  if (isClosed) {
    return (
      <div className="min-h-screen bg-[#000000] text-white font-sans selection:bg-[#D4AF37]/30 flex flex-col justify-between relative overflow-hidden">
        {/* Dark Moody Background Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#000000] to-[#000000] z-0" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 z-0 mix-blend-screen" />
        
        {/* Floating gradient orbs for subtle depth */}
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-[#D4AF37]/10 rounded-full blur-[150px] z-0" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-[#B8860B]/5 rounded-full blur-[150px] z-0" />

        <div className="relative z-10 flex-grow flex flex-col items-center justify-center text-center px-4 py-20 w-full max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="flex flex-col items-center w-full"
          >
            {/* Top Logo & Branding */}
            <div className="flex flex-col items-center mb-16">
              <img 
                src={process.env.PUBLIC_URL + '/club.png'} 
                alt="Leo Club Logo" 
                className="w-16 h-16 object-contain mb-4"
              />
              <h2 className="text-xl md:text-2xl font-extrabold tracking-widest uppercase text-white mb-1">
                Leo Club Of SLIIT
              </h2>
              <p className="text-sm md:text-base font-bold text-[#D4AF37] tracking-[0.2em] uppercase">
                Legacy Continuous
              </p>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-8">
              The deadline has passed, but the excitement has{' '}
              <span className="text-[#D4AF37]">just begun</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-gray-300 font-medium mb-16">
              Wishing the best to all who stepped up for leadership!
            </p>

            {/* Hashtag Footer */}
            <div className="text-2xl md:text-3xl font-bold text-[#D4AF37] tracking-wider">
              # SLIITලියෝකුලේ
            </div>
          </motion.div>
        </div>

        <div className="relative z-10 w-full border-t border-white/5 bg-[#02050a]/80 backdrop-blur-md">
          <Footer clubName={CLUB_NAME} boardYear={BOARD_YEAR} />
        </div>
      </div>
    );
  }

  // ── Active Application Screen ──
  return (
    <div className="min-h-screen bg-[#030303] text-gray-100 font-sans selection:bg-[#D4AF37]/30">
      <BackgroundBlobs />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Navbar />

        <HeroSection boardYear={BOARD_YEAR} deadline={deadline} />

        <PositionsGrid positions={positions} deadline={deadline} />

        <CallToAction boardYear={BOARD_YEAR} />
      </div>

      <Footer clubName={CLUB_NAME} boardYear={BOARD_YEAR} />
    </div>
  );
};

export default App;
